<template>
  <!--
    Book an IMPROVE Live webinar on the website, no account (Muhammad, 6 Oct
    2026; plan docs/plans/improve-live-webinars-2026-10.md in the ops repo).
    A month calendar marks the upcoming webinars; the visitor picks one and
    gives name, work email, company and phone. The emails carry a private
    link (?booking=<token>) that opens this same component to change the date
    or cancel. Nothing loads while the page is prerendered, so no dates are
    baked into the HTML.
  -->
  <section id="book" class="wbk" aria-live="polite">
    <v-container>
      <div class="wbk__card">
        <!-- A div, not <header>: the site's global header rule fixes every <header> to the top. -->
        <div class="wbk__head">
          <p class="wbk__eyebrow">IMPROVE Live webinar</p>
          <h2 class="font-sora wbk__title">{{ heading }}</h2>
          <p v-if="intro" class="wbk__lead">{{ intro }}</p>
        </div>

        <div v-if="mode === 'loading'" class="wbk__centre">
          <v-progress-circular indeterminate color="#08a0a8" />
        </div>

        <!-- Nothing open to book -->
        <div v-else-if="mode === 'off'" class="wbk__centre wbk__message">
          <p>There are no webinar dates open right now.</p>
          <p>
            <router-link to="/contact" class="wbk__btn wbk__btn--ghost">Contact us</router-link>
          </p>
        </div>

        <!-- Booked (just now, or opened from the private link) -->
        <div v-else-if="(mode === 'booked' || mode === 'manage') && !changing" class="wbk__booked">
          <div class="wbk__when">
            <v-icon size="34" color="#2fa84f">mdi-calendar-check</v-icon>
            <div>
              <p class="wbk__when-date">{{ longDate(booking.label) }}</p>
              <p class="wbk__when-time">{{ booking.time }} to {{ endTime(booking.time) }} UK time</p>
            </div>
          </div>
          <p v-if="mode === 'booked'" class="wbk__note">
            We have emailed your calendar invite, the Teams link and a private link to change or cancel to {{ bookedEmail }}.
          </p>
          <div class="wbk__actions">
            <a :href="booking.join_url" target="_blank" rel="noopener" class="wbk__btn wbk__btn--primary">Join on Teams</a>
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <button type="button" class="wbk__btn wbk__btn--ghost" v-on="on">Add to calendar</button>
              </template>
              <v-list dense>
                <v-list-item :href="outlookLink" target="_blank" rel="noopener">
                  <v-list-item-title>Outlook</v-list-item-title>
                </v-list-item>
                <v-list-item :href="googleLink" target="_blank" rel="noopener">
                  <v-list-item-title>Google Calendar</v-list-item-title>
                </v-list-item>
                <v-list-item @click="downloadIcs">
                  <v-list-item-title>Apple or another calendar (.ics file)</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div v-if="token" class="wbk__links">
            <button type="button" class="wbk__link" @click="startChange">Change date</button>
            <button type="button" class="wbk__link wbk__link--danger" @click="confirmingCancel = true">Cancel booking</button>
          </div>
          <div v-if="confirmingCancel" class="wbk__confirm">
            <p>Cancel your place on {{ booking.short_label }}?</p>
            <button type="button" class="wbk__btn wbk__btn--danger" :disabled="saving" @click="cancel">Yes, cancel it</button>
            <button type="button" class="wbk__link" @click="confirmingCancel = false">Keep my place</button>
          </div>
        </div>

        <!-- The email already holds a booking -->
        <div v-else-if="mode === 'already'" class="wbk__centre wbk__message">
          <p>
            This email address already has a place booked. We have emailed the details again,
            with a private link to change the date or cancel.
          </p>
          <p><button type="button" class="wbk__link" @click="mode = 'book'">Use a different email</button></p>
        </div>

        <!-- Cancelled just now -->
        <div v-else-if="mode === 'cancelled'" class="wbk__centre wbk__message">
          <p>Your booking is cancelled, and the cancellation email removes it from your calendar.</p>
          <p><button type="button" class="wbk__btn wbk__btn--primary" @click="bookAgain">Book another date</button></p>
        </div>

        <!-- Choose a date (and, for a new booking, your details) -->
        <div v-else class="wbk__pick">
          <div class="wbk__calendar-col">
            <v-date-picker
              v-model="date"
              class="wbk__calendar"
              no-title
              full-width
              first-day-of-week="1"
              locale="en-gb"
              color="#002458"
              :min="minDate"
              :max="maxDate"
              :picker-date.sync="month"
              :allowed-dates="isOpenDate"
              :events="eventDates"
              :event-color="eventColor"
            />
            <div class="wbk__legend">
              <span><i class="wbk__dot wbk__dot--open"></i>Places left</span>
              <span><i class="wbk__dot wbk__dot--full"></i>Full</span>
              <span v-if="changing"><i class="wbk__dot wbk__dot--mine"></i>Your booking</span>
            </div>
          </div>

          <div class="wbk__form-col">
            <p v-if="notice" class="wbk__notice">{{ notice }}</p>
            <div v-if="selected" class="wbk__slot">
              <p class="wbk__slot-date">{{ longDate(selected.label) }}</p>
              <p class="wbk__slot-time">{{ selected.time }} to {{ endTime(selected.time) }} UK time</p>
              <p class="wbk__slot-places" :class="{ 'is-low': selected.seats_remaining <= 3 }">{{ placesText(selected) }}</p>
            </div>
            <p v-else class="wbk__hint">Choose one of the highlighted dates.</p>

            <template v-if="changing">
              <button type="button" class="wbk__btn wbk__btn--primary wbk__btn--block" :disabled="!selected || saving" @click="move">
                Move my booking here
              </button>
              <button type="button" class="wbk__link" @click="changing = false">Keep {{ booking.short_label }}</button>
            </template>

            <v-form v-else ref="form" class="wbk__form" @submit.prevent="book">
              <!-- Honeypot: off screen, invisible to people. The API books nothing when it is filled. -->
              <input v-model="form.company_website" type="text" name="company_website" tabindex="-1" autocomplete="off" aria-hidden="true" class="wbk__trap" />
              <div class="wbk__row">
                <v-text-field id="wbk-first-name" v-model="form.first_name" label="First name" outlined dense autocomplete="given-name" :rules="[rules.required, rules.max100]" />
                <v-text-field id="wbk-last-name" v-model="form.last_name" label="Last name" outlined dense autocomplete="family-name" :rules="[rules.required, rules.max100]" />
              </div>
              <v-text-field id="wbk-email" v-model="form.email" label="Work email" type="email" outlined dense autocomplete="email" :rules="[rules.required, rules.email]" />
              <v-text-field id="wbk-company" v-model="form.company_name" label="Company" outlined dense autocomplete="organization" :rules="[rules.required, rules.max191]" />
              <v-text-field id="wbk-phone" v-model="form.phone" label="Phone" type="tel" outlined dense autocomplete="tel" :rules="[rules.required, rules.phone]" />
              <p class="wbk__privacy">
                We use these details to manage your booking, send your invite and a reminder, and so the
                IMPROVE team can follow up. See our <router-link to="/privacy-policy">Privacy Policy</router-link>.
              </p>
              <button type="submit" class="wbk__btn wbk__btn--primary wbk__btn--block" :disabled="!selected || saving">
                {{ saving ? 'Booking...' : 'Book my place' }}
              </button>
            </v-form>
          </div>
        </div>

        <p v-if="error" class="wbk__error" role="alert">{{ error }}</p>
      </div>
    </v-container>
  </section>
</template>

<script>
import ImproveLiveAPI from '@/Api/improveLive.js';
import { downloadIcsFile, googleCalendarLink, outlookCalendarLink } from '@/utils/calendarLinks';

const COLOUR_OPEN = '#2fa84f';
const COLOUR_FULL = '#b0b8c4';
const COLOUR_MINE = '#002458';
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function emptyForm() {
  return { first_name: '', last_name: '', email: '', company_name: '', phone: '', company_website: '' };
}

export default {
  name: 'WebinarBooking',
  data() {
    return {
      mode: 'loading',
      durationMinutes: 20,
      sessions: [],
      date: null,
      month: null,
      form: emptyForm(),
      booking: null,
      bookedEmail: '',
      token: null,
      changing: false,
      confirmingCancel: false,
      saving: false,
      notice: '',
      error: '',
      rules: {
        required: (v) => !!String(v || '').trim() || 'Required.',
        max100: (v) => String(v || '').length <= 100 || 'Too long.',
        max191: (v) => String(v || '').length <= 191 || 'Too long.',
        email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim()) || 'Enter a valid email address.',
        phone: (v) => {
          const digits = String(v || '').replace(/\D/g, '');
          return (/^[\d\s+()-]+$/.test(String(v || '').trim()) && digits.length >= 7 && digits.length <= 15) || 'Enter a valid phone number.';
        },
      },
    };
  },
  computed: {
    heading() {
      if (this.mode === 'booked') return 'You are booked';
      if (this.mode === 'manage' && !this.changing) return 'Your webinar booking';
      if (this.changing) return 'Choose a new date';
      return 'Book your place';
    },
    intro() {
      const what = 'A ' + this.durationMinutes + ' minute live demonstration of IMPROVE, with time for your questions, on Microsoft Teams.';
      return this.scheduleText ? what + ' ' + this.scheduleText : what;
    },
    // "Tuesdays at 11:00 and Thursdays at 14:00 (UK time).", from the dates offered.
    scheduleText() {
      const seen = {};
      const parts = [];
      this.sessions.forEach((s) => {
        const key = DAY_NAMES[new Date(s.date + 'T12:00:00Z').getUTCDay()] + 's at ' + s.time;
        if (!seen[key]) {
          seen[key] = true;
          parts.push(key);
        }
      });
      return parts.length ? parts.join(' and ') + ' (UK time).' : '';
    },
    sessionsByDate() {
      const byDate = {};
      this.sessions.forEach((s) => {
        (byDate[s.date] = byDate[s.date] || []).push(s);
      });
      return byDate;
    },
    eventDates() {
      return Object.keys(this.sessionsByDate);
    },
    minDate() {
      return this.eventDates.length ? this.eventDates[0] : undefined;
    },
    maxDate() {
      return this.eventDates.length ? this.eventDates[this.eventDates.length - 1] : undefined;
    },
    // One webinar a day today (Tuesday or Thursday); the first that can be booked.
    selected() {
      const list = (this.date && this.sessionsByDate[this.date]) || [];
      return list.find((s) => !s.is_full && !this.isMine(s)) || null;
    },
    calendarEvent() {
      const b = this.booking;
      if (!b) return null;
      return {
        uid: b.calendar.uid,
        sequence: b.calendar.sequence,
        title: b.calendar.title,
        details: b.calendar.details,
        start: b.session_at,
        end: b.ends_at,
        url: b.join_url,
      };
    },
    outlookLink() {
      return this.calendarEvent ? outlookCalendarLink(this.calendarEvent) : '#';
    },
    googleLink() {
      return this.calendarEvent ? googleCalendarLink(this.calendarEvent) : '#';
    },
  },
  mounted() {
    // While scripts/prerender.js snapshots the page, show the loading state
    // only: dates fetched at build time would be stale by the time anyone
    // reads the HTML.
    if (typeof window !== 'undefined' && window.__PRERENDER_HOST__) return;

    const token = this.$route.query.booking;
    const load = token ? this.openBooking(String(token)) : this.loadSessions();
    load.then(() => {
      if (token || this.$route.query.date || this.$route.hash === '#book') {
        this.$nextTick(() => this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      }
    });
  },
  methods: {
    apply(data) {
      this.durationMinutes = Number(data.duration_minutes) || 20;
      this.sessions = data.enabled && Array.isArray(data.sessions) ? data.sessions : [];
      if (this.date && !this.sessionsByDate[this.date]) this.date = null;
      if (this.minDate && (!this.month || this.month < this.minDate.slice(0, 7))) this.month = this.minDate.slice(0, 7);
    },
    loadSessions() {
      return ImproveLiveAPI.sessions()
        .then((data) => {
          this.apply(data);
          const wanted = this.$route.query.date;
          if (wanted && this.isOpenDate(String(wanted))) {
            this.date = String(wanted);
            this.month = this.date.slice(0, 7);
          }
          if (this.mode === 'loading') this.mode = this.sessions.length ? 'book' : 'off';
        })
        .catch(() => {
          this.mode = 'off';
        });
    },
    openBooking(token) {
      return ImproveLiveAPI.booking(token)
        .then((data) => {
          this.apply(data);
          if (data.status === 'booked' && data.booking) {
            this.token = token;
            this.booking = data.booking;
            this.mode = 'manage';
          } else {
            this.notice = data.status === 'cancelled'
              ? 'That booking has been cancelled. You are welcome to book another date.'
              : 'That webinar has already taken place. You are welcome to book another date.';
            this.mode = this.sessions.length ? 'book' : 'off';
          }
        })
        .catch(() => {
          this.notice = 'We could not find that booking. You are welcome to book a date below.';
          return this.loadSessions().then(() => {
            if (this.mode === 'loading') this.mode = this.sessions.length ? 'book' : 'off';
          });
        });
    },
    isMine(s) {
      return !!this.booking && this.changing && this.booking.session_at === s.session_at;
    },
    isOpenDate(date) {
      return (this.sessionsByDate[date] || []).some((s) => !s.is_full && !this.isMine(s));
    },
    eventColor(date) {
      const list = this.sessionsByDate[date] || [];
      if (list.some(this.isMine)) return COLOUR_MINE;
      return list.every((s) => s.is_full) ? COLOUR_FULL : COLOUR_OPEN;
    },
    longDate(label) {
      return String(label || '').split(', ')[0];
    },
    endTime(time) {
      const parts = String(time || '0:0').split(':');
      const total = Number(parts[0]) * 60 + Number(parts[1]) + this.durationMinutes;
      const pad = (n) => (n < 10 ? '0' : '') + n;
      return pad(Math.floor(total / 60) % 24) + ':' + pad(total % 60);
    },
    placesText(s) {
      if (s.is_full) return 'Fully booked';
      return s.seats_remaining + (s.seats_remaining === 1 ? ' place left' : ' places left');
    },
    book() {
      this.error = '';
      if (!this.selected) {
        this.error = 'Choose a date first.';
        return;
      }
      // This site's Vuetify (2.0) stops validate() at the first invalid
      // field; check every one so all the problems show at once.
      const valid = this.$refs.form.inputs.map((input) => input.validate(true)).every(Boolean);
      if (!valid) return;
      this.saving = true;
      ImproveLiveAPI.book({
        session_at: this.selected.session_at,
        first_name: this.form.first_name.trim(),
        last_name: this.form.last_name.trim(),
        email: this.form.email.trim(),
        company_name: this.form.company_name.trim(),
        phone: this.form.phone.trim(),
        company_website: this.form.company_website,
      })
        .then((data) => {
          if (data.status === 'already_booked') {
            this.mode = 'already';
            return;
          }
          this.bookedEmail = this.form.email.trim();
          this.booking = data.booking;
          this.token = data.manage_token || null;
          this.form = emptyForm();
          this.date = null;
          this.notice = '';
          this.mode = this.booking ? 'booked' : 'already';
        })
        .catch((err) => this.fail(err))
        .then(() => {
          this.saving = false;
        });
    },
    startChange() {
      this.changing = true;
      this.confirmingCancel = false;
      this.error = '';
      this.date = null;
      this.month = this.minDate ? this.minDate.slice(0, 7) : null;
    },
    move() {
      if (!this.selected || !this.token) return;
      this.saving = true;
      this.error = '';
      ImproveLiveAPI.move(this.token, this.selected.session_at)
        .then((data) => {
          this.apply(data);
          this.booking = data.booking;
          this.changing = false;
          this.date = null;
          this.mode = 'manage';
        })
        .catch((err) => this.fail(err))
        .then(() => {
          this.saving = false;
        });
    },
    cancel() {
      if (!this.token) return;
      this.saving = true;
      this.error = '';
      ImproveLiveAPI.cancel(this.token)
        .then((data) => {
          this.apply(data);
          this.booking = null;
          this.token = null;
          this.confirmingCancel = false;
          this.mode = 'cancelled';
        })
        .catch((err) => this.fail(err))
        .then(() => {
          this.saving = false;
        });
    },
    bookAgain() {
      this.notice = '';
      this.date = null;
      this.mode = this.sessions.length ? 'book' : 'off';
    },
    downloadIcs() {
      if (this.calendarEvent) downloadIcsFile(this.calendarEvent, 'improve-live-webinar.ics');
    },
    fail(err) {
      const data = err && err.response && err.response.data;
      const fieldErrors = data && data.errors ? Object.values(data.errors).map((e) => [].concat(e)[0]) : [];
      this.error = fieldErrors[0] || (data && data.message) || 'Something went wrong. Please try again.';
      // A full or closed date, or a booking changed elsewhere: show the latest.
      this.loadSessions();
    },
  },
};
</script>

<style scoped>
.wbk {
  padding: 8px 0 40px;
}

/* The width lives on the card: the site forces .container to 1420px !important. */
.wbk__card {
  max-width: 980px;
  margin: 0 auto;
  padding: 28px 28px 24px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e3e9f1;
  box-shadow: 0 10px 30px rgba(0, 36, 88, 0.06);
}

.wbk__head {
  margin-bottom: 18px;
}

.wbk__eyebrow {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-teal, #08a0a8);
}

.wbk__title {
  margin: 0 0 8px;
  font-size: 26px;
  line-height: 1.25;
  font-weight: 700;
  color: var(--brand-navy, #002458);
}

.wbk__lead {
  margin: 0;
  max-width: 62ch;
  font-size: 15px;
  line-height: 1.55;
  color: var(--brand-grey, #6b7280);
}

.wbk__centre {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  text-align: center;
}

.wbk__message p {
  max-width: 52ch;
  margin: 0 0 14px;
  color: var(--brand-navy, #002458);
  font-size: 16px;
  line-height: 1.55;
}

.wbk__pick {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 28px;
  align-items: start;
}

.wbk__calendar {
  box-shadow: none !important;
  border: 1px solid #e3e9f1;
  border-radius: 12px !important;
}

.wbk__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  font-size: 12px;
  color: var(--brand-grey, #6b7280);
}

/* The site sets every a, span and p to 16px; these follow their block. */
.wbk__legend span,
.wbk__privacy a {
  font-size: inherit;
  line-height: inherit;
}

.wbk__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  border-radius: 50%;
  vertical-align: 1px;
}

.wbk__dot--open {
  background: #2fa84f;
}

.wbk__dot--full {
  background: #b0b8c4;
}

.wbk__dot--mine {
  background: #002458;
}

.wbk__notice {
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff7ec;
  color: #7a4a12;
  font-size: 14px;
}

.wbk__slot {
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f6f8fb;
  border: 1px solid #e8edf4;
}

.wbk__slot-date {
  margin: 0 0 2px;
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-navy, #002458);
}

.wbk__slot-time {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 600;
  color: #24364d;
}

.wbk__slot-places {
  margin: 0;
  font-size: 13px;
  color: #2f7a52;
}

.wbk__slot-places.is-low {
  color: #c8641a;
  font-weight: 600;
}

.wbk__hint {
  margin: 0 0 16px;
  font-size: 14px;
  color: var(--brand-grey, #6b7280);
}

.wbk__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.wbk__trap {
  position: absolute;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
}

.wbk__privacy {
  margin: 0 0 14px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--brand-grey, #6b7280);
}

.wbk__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 20px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.wbk__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.wbk__btn--block {
  width: 100%;
}

.wbk__btn--primary {
  background: var(--brand-navy, #002458);
  color: #fff !important;
}

.wbk__btn--primary:hover:not(:disabled) {
  background: var(--brand-teal, #08a0a8);
}

.wbk__btn--ghost {
  background: #fff;
  color: var(--brand-navy, #002458) !important;
  border-color: #d5dde8;
}

.wbk__btn--ghost:hover {
  border-color: var(--brand-teal, #08a0a8);
  color: var(--brand-teal, #08a0a8) !important;
}

.wbk__btn--danger {
  background: #b3261e;
  color: #fff !important;
  min-height: 38px;
  font-size: 14px;
}

.wbk__btn:focus-visible,
.wbk__link:focus-visible {
  outline: 2px solid var(--brand-teal, #08a0a8);
  outline-offset: 2px;
}

.wbk__link {
  margin-top: 10px;
  padding: 4px 6px;
  border: 0;
  background: none;
  color: var(--brand-navy, #002458);
  font-weight: 600;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
}

.wbk__link--danger {
  color: #b3261e;
}

.wbk__booked {
  max-width: 600px;
}

.wbk__when {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  background: #eef6f1;
  border: 1px solid rgba(123, 200, 163, 0.5);
}

.wbk__when-date {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: var(--brand-navy, #002458);
}

.wbk__when-time {
  margin: 2px 0 0;
  font-size: 14px;
  color: #3d5068;
}

.wbk__note {
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--brand-grey, #6b7280);
}

.wbk__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.wbk__links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wbk__confirm {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fdf1f0;
  border: 1px solid rgba(179, 38, 30, 0.2);
}

.wbk__confirm p {
  margin: 0 0 8px;
  font-size: 14px;
  color: #6b1d18;
}

.wbk__error {
  margin: 16px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fdf1f0;
  color: #8a1f17;
  font-size: 14px;
}

@media (max-width: 767px) {
  .wbk__card {
    padding: 20px 16px;
  }

  .wbk__pick {
    grid-template-columns: 1fr;
  }

  .wbk__row {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
</style>
